import SuperFetch from 'library/helpers/superFetch';
import { all, call, put, select, takeLatest, takeEvery } from 'redux-saga/effects';
import { Notification } from 'zComponents/atoms';
import { slackActions } from './slice';

/**
 * Slack integration saga (Phase 1). CRUD against the v5 /slack API — server enforces org-wide-admin
 * + the `slackIntegration` flag; a non-admin/flag-off request 403s and the saga surfaces that via
 * the normal error toast. The OAuth install itself is a browser redirect (the component opens the
 * URL from loadAvailable's sibling /slack/oauth/start), not a saga call.
 */
const selectOrgID = (state) => state.Auth.selectedProfile.orgID;
const okStatus = (res) => res && (res.statusCode === 200 || res.statusCode === 201);

function* fetchConnection() {
	const orgID = yield select(selectOrgID);
	yield put(slackActions.loadConnectionStart({ orgID }));
	try {
		const res = yield call(() => SuperFetch.get('/slack'));
		if (!okStatus(res)) throw new Error(res.message || 'Failed to load the Slack connection.');
		yield put(slackActions.loadConnectionSuccess({ orgID, ...(res.data || {}) }));
	} catch (error) {
		yield put(slackActions.loadConnectionError({ orgID, error: error.message || String(error) }));
	}
}

function* fetchAvailable() {
	const orgID = yield select(selectOrgID);
	try {
		const res = yield call(() => SuperFetch.get('/slack/channels/available'));
		if (!okStatus(res)) throw new Error(res.message || 'Failed to load Slack channels.');
		yield put(slackActions.loadAvailableSuccess({ orgID, channels: res.data || [] }));
	} catch (error) {
		Notification('error', 'Could not load Slack channels.');
		yield put(slackActions.loadAvailableError({ orgID }));
	}
}

function* addChannel(action) {
	const orgID = yield select(selectOrgID);
	const { channelID, channelName } = action.payload;
	try {
		const res = yield call(() => SuperFetch.post('/slack/channels', { channelID, channelName }));
		if (!okStatus(res)) throw new Error(res.message || 'Failed to add the channel.');
		yield put(slackActions.addChannelSuccess({ orgID, channel: res.data }));
		Notification('success', 'Slack channel added.');
	} catch (error) {
		Notification('error', 'Could not add the Slack channel.');
		yield put(slackActions.addChannelError({ orgID }));
	}
}

function* removeChannel(action) {
	const orgID = yield select(selectOrgID);
	const { slackChannelID } = action.payload;
	try {
		const res = yield call(() => SuperFetch.delete(`/slack/channels/${slackChannelID}`));
		if (!okStatus(res)) throw new Error(res.message || 'Failed to remove the channel.');
		yield put(slackActions.removeChannelSuccess({ orgID, slackChannelID }));
		Notification('success', 'Slack channel removed.');
	} catch (error) {
		Notification('error', 'Could not remove the Slack channel.');
		yield put(slackActions.removeChannelError({ orgID }));
	}
}

function* disconnect() {
	const orgID = yield select(selectOrgID);
	try {
		const res = yield call(() => SuperFetch.delete('/slack'));
		if (!okStatus(res)) throw new Error(res.message || 'Failed to disconnect.');
		yield put(slackActions.disconnectSuccess({ orgID }));
		Notification('success', 'Slack disconnected.');
	} catch (error) {
		Notification('error', 'Could not disconnect Slack.');
		yield put(slackActions.disconnectError({ orgID }));
	}
}

export default function* slackSaga() {
	yield all([
		takeLatest(slackActions.loadConnection, fetchConnection),
		takeLatest(slackActions.loadAvailable, fetchAvailable),
		takeEvery(slackActions.addChannel, addChannel),
		takeEvery(slackActions.removeChannel, removeChannel),
		takeEvery(slackActions.disconnect, disconnect)
	]);
}
